import { useDeferredValue, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCheck,
  ClipboardCheck,
  Save,
  Scale,
  Sigma,
  TriangleAlert,
  XCircle,
} from 'lucide-react';
import { PERMISSIONS, StocktakeStatus, type StocktakeLineSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { StatCard } from '@/components/ui/StatCard';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCancelStocktakeMutation,
  usePostStocktakeMutation,
  useRecordCountsMutation,
  useStocktakeLinesQuery,
  useStocktakeQuery,
} from '../api/stocktake.api';
import { StocktakeStatusBadge } from '../components/StocktakeStatusBadge';

const PAGE_SIZE = 25;

type SheetFilter = '' | 'variances' | 'uncounted';

/**
 * The count sheet.
 *
 * Figures are typed locally and saved in a batch: a counter walking a shelf should not wait for a
 * round trip per line. Nothing here touches stock — that only happens when the session is posted.
 */
export function StocktakeSheetPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { sessionId = '' } = useParams<{ sessionId: string }>();

  const [filter, setFilter] = useState<SheetFilter>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'post' | 'postWithZeros' | 'cancel' | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const deferredSearch = useDeferredValue(search);

  const session = useStocktakeQuery(sessionId);
  const lines = useStocktakeLinesQuery(sessionId, {
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    variancesOnly: filter === 'variances',
    uncountedOnly: filter === 'uncounted',
  });

  const recordCounts = useRecordCountsMutation(sessionId);
  const postSession = usePostStocktakeMutation(sessionId);
  const cancelSession = useCancelStocktakeMutation(sessionId);

  // A refetch must not clobber figures the counter has typed but not yet saved.
  useEffect(() => {
    setDraft({});
  }, [sessionId]);

  const detail = session.data;
  const isOpen = detail?.status === StocktakeStatus.OPEN;
  const canCount = can(PERMISSIONS.INVENTORY_ADJUST);
  const pendingCount = Object.keys(draft).length;

  const fail = (mutationError: unknown, fallback: string) =>
    setError(apiErrorMessage(mutationError) ?? fallback);

  const handleSave = () => {
    setError(null);
    setFeedback(null);

    const payload = Object.entries(draft)
      .map(([lineId, value]) => ({ lineId, countedQuantity: Number(value) }))
      .filter((line) => Number.isInteger(line.countedQuantity) && line.countedQuantity >= 0);

    if (payload.length !== pendingCount) {
      setError(t('inventory.validation.quantityInvalid'));
      return;
    }
    if (payload.length === 0) return;

    recordCounts.mutate(payload, {
      onSuccess: () => {
        setDraft({});
        setFeedback(t('inventory.stocktake.saved', { count: payload.length }));
      },
      onError: (mutationError) => fail(mutationError, t('inventory.stocktake.saveError')),
    });
  };

  const handlePost = (treatUncountedAsZero: boolean) => {
    setError(null);
    setConfirm(null);

    postSession.mutate(
      { treatUncountedAsZero },
      {
        onSuccess: (result) =>
          setFeedback(
            t('inventory.stocktake.posted', {
              reference: result.reference,
              count: result.varianceLineCount,
            }),
          ),
        onError: (mutationError) => fail(mutationError, t('inventory.stocktake.postError')),
      },
    );
  };

  const handleCancel = () => {
    setError(null);

    if (cancelReason.trim().length < 3) {
      setError(t('inventory.stocktake.validation.reasonRequired'));
      return;
    }

    cancelSession.mutate(cancelReason.trim(), {
      onSuccess: () => {
        setConfirm(null);
        navigate('/inventory/stocktake');
      },
      onError: (mutationError) => fail(mutationError, t('inventory.stocktake.cancelError')),
    });
  };

  const columns: Column<StocktakeLineSummary>[] = [
    {
      key: 'product',
      header: t('inventory.fields.product'),
      primary: true,
      render: (line) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium">{line.productName}</span>
          <span className="text-muted text-xs">
            <span className="font-mono" dir="ltr">
              {line.sku}
            </span>
            {line.batchNumber ? <span className="mx-1.5">· {line.batchNumber}</span> : null}
          </span>
        </span>
      ),
    },
    {
      key: 'expected',
      header: t('inventory.stocktake.fields.expected'),
      align: 'end',
      render: (line) => (
        <span className="flex flex-col items-end">
          <span className="text-ink" dir="ltr">
            {line.expectedQuantityAtCount ?? line.expectedQuantityAtOpen}
          </span>
          {/* Only worth showing when a legitimate movement shifted the figure mid-count. */}
          {line.movementBeforeCount ? (
            <span className="text-muted text-xs" dir="ltr">
              {t('inventory.stocktake.movedSinceOpen', {
                open: line.expectedQuantityAtOpen,
                delta:
                  line.movementBeforeCount > 0
                    ? `+${line.movementBeforeCount}`
                    : line.movementBeforeCount,
              })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'counted',
      header: t('inventory.stocktake.fields.counted'),
      align: 'end',
      render: (line) =>
        isOpen && canCount ? (
          <span className="inline-block w-24">
            <Input
              type="number"
              min={0}
              value={draft[line.id] ?? (line.countedQuantity ?? '').toString()}
              aria-label={t('inventory.stocktake.countedFor', { sku: line.sku })}
              onChange={(event) =>
                setDraft((current) => ({ ...current, [line.id]: event.target.value }))
              }
            />
          </span>
        ) : (
          <span className="text-ink" dir="ltr">
            {line.countedQuantity ?? '—'}
          </span>
        ),
    },
    {
      key: 'variance',
      header: t('inventory.stocktake.fields.variance'),
      align: 'end',
      render: (line) =>
        line.varianceAtCount === null ? (
          <span className="text-muted">—</span>
        ) : (
          <span
            className={
              line.varianceAtCount === 0 ? 'text-muted' : 'text-sm font-semibold text-amber-700'
            }
            dir="ltr"
          >
            {line.varianceAtCount > 0 ? `+${line.varianceAtCount}` : line.varianceAtCount}
          </span>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={detail?.reference ?? t('inventory.stocktake.title')}
        subtitle={
          detail
            ? [detail.name, detail.branchName].filter(Boolean).join(' · ')
            : t('inventory.stocktake.subtitle')
        }
        actions={
          <Button variant="secondary" onClick={() => navigate('/inventory/stocktake')}>
            <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />
            {t('inventory.stocktake.backToList')}
          </Button>
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      {detail ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <StocktakeStatusBadge status={detail.status} />
            {detail.scopeProductType ? (
              <span className="text-muted text-xs">
                {t('inventory.stocktake.scopedTo', {
                  scope: t(`catalog.types.${detail.scopeProductType}`),
                })}
              </span>
            ) : null}
            {detail.scopeCategoryName ? (
              <span className="text-muted text-xs">
                {t('inventory.stocktake.scopedTo', { scope: detail.scopeCategoryName })}
              </span>
            ) : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={ClipboardCheck}
              label={t('inventory.stocktake.stats.counted')}
              value={`${detail.countedLineCount} / ${detail.lineCount}`}
            />
            <StatCard
              icon={TriangleAlert}
              label={t('inventory.stocktake.stats.variances')}
              value={detail.varianceLineCount}
              tone={detail.varianceLineCount > 0 ? 'warning' : 'neutral'}
            />
            <StatCard
              icon={Sigma}
              label={t('inventory.stocktake.stats.net')}
              value={detail.netVariance > 0 ? `+${detail.netVariance}` : String(detail.netVariance)}
            />
            <StatCard
              icon={Scale}
              label={t('inventory.stocktake.stats.absolute')}
              value={detail.absoluteVariance}
            />
          </div>

          {isOpen ? (
            <Alert tone="info">
              {t('inventory.stocktake.openNotice')} {t('inventory.stocktake.countTimeNotice')}
            </Alert>
          ) : detail.status === StocktakeStatus.POSTED ? (
            <Alert tone="success">
              {t('inventory.stocktake.postedNotice', { count: detail.varianceLineCount })}
            </Alert>
          ) : (
            <Alert tone="warning">
              {t('inventory.stocktake.cancelledNotice', {
                reason: detail.cancelledReason ?? '',
              })}
            </Alert>
          )}
        </>
      ) : null}

      <Card>
        <CardHeader
          title={t('inventory.stocktake.sheet')}
          subtitle={
            lines.data
              ? t('inventory.stocktake.lineCount', { count: lines.data.pagination.totalItems })
              : undefined
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 sm:w-52">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder={t('inventory.stock.searchPlaceholder')}
                  aria-label={t('inventory.stock.searchLabel')}
                />
              </div>
              <div className="w-40">
                <Select
                  options={[
                    { value: '', label: t('inventory.stocktake.filters.allLines') },
                    { value: 'variances', label: t('inventory.stocktake.filters.variances') },
                    { value: 'uncounted', label: t('inventory.stocktake.filters.uncounted') },
                  ]}
                  value={filter}
                  aria-label={t('inventory.stocktake.filters.allLines')}
                  onChange={(event) => {
                    setFilter(event.target.value as SheetFilter);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          }
        />

        <DataTable
          caption={t('inventory.stocktake.sheetCaption')}
          columns={columns}
          rows={lines.data?.items}
          rowKey={(line) => line.id}
          isLoading={lines.isPending}
          isError={lines.isError}
          errorMessage={apiErrorMessage(lines.error) ?? t('inventory.stocktake.loadError')}
          onRetry={() => void lines.refetch()}
          emptyTitle={t('inventory.stocktake.noLines')}
          emptyDescription={t('inventory.stocktake.noLinesHint')}
        />

        {lines.data ? (
          <Pagination
            pagination={lines.data.pagination}
            onPageChange={setPage}
            isBusy={lines.isFetching}
          />
        ) : null}
      </Card>

      {isOpen && canCount ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-muted me-auto text-xs">
            {pendingCount > 0
              ? t('inventory.stocktake.unsaved', { count: pendingCount })
              : t('inventory.stocktake.allSaved')}
          </span>

          <Button variant="secondary" onClick={() => setConfirm('cancel')}>
            <XCircle className="size-4" aria-hidden />
            {t('inventory.stocktake.cancel')}
          </Button>
          <Button
            variant="secondary"
            isLoading={recordCounts.isPending}
            disabled={pendingCount === 0}
            onClick={handleSave}
          >
            <Save className="size-4" aria-hidden />
            {t('inventory.stocktake.saveCounts')}
          </Button>
          <Button
            isLoading={postSession.isPending}
            onClick={() => setConfirm(detail?.isComplete ? 'post' : 'postWithZeros')}
          >
            <CheckCheck className="size-4" aria-hidden />
            {t('inventory.stocktake.post')}
          </Button>
        </div>
      ) : null}

      {confirm === 'cancel' ? (
        <Card>
          <CardHeader
            title={t('inventory.stocktake.confirmCancelTitle')}
            subtitle={t('inventory.stocktake.confirmCancelMessage')}
          />
          <div className="space-y-3 p-4 pt-0">
            <Input
              value={cancelReason}
              label={t('inventory.stocktake.fields.cancelReason')}
              onChange={(event) => setCancelReason(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" isLoading={cancelSession.isPending} onClick={handleCancel}>
                {t('inventory.stocktake.cancel')}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <ConfirmDialog
        isOpen={confirm === 'post'}
        title={t('inventory.stocktake.confirmPostTitle')}
        message={t('inventory.stocktake.confirmPostMessage', {
          count: detail?.varianceLineCount ?? 0,
        })}
        confirmLabel={t('inventory.stocktake.post')}
        isConfirming={postSession.isPending}
        onConfirm={() => handlePost(false)}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        isOpen={confirm === 'postWithZeros'}
        title={t('inventory.stocktake.confirmPartialTitle')}
        message={t('inventory.stocktake.confirmPartialMessage', {
          counted: detail?.countedLineCount ?? 0,
          total: detail?.lineCount ?? 0,
        })}
        confirmLabel={t('inventory.stocktake.postCountedOnly')}
        isConfirming={postSession.isPending}
        onConfirm={() => handlePost(false)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
